/* eslint-disable */
import { useState } from 'react';
import { deviceRegistry } from '@/devices/catalog';
import { DEVICE_TYPES, type DeviceType } from '@/devices/types';
import { useT } from '@/i18n/useT';
import type { TranslationKey } from '@/i18n';
import { useProjectStore } from '@/store/projectStore';
import { useUiStore, type SelectableObjectType } from '@/store/uiStore';
import { useSimulationStore } from '@/store/simulationStore';
import { EmptyState } from '@/ui/components/EmptyState';
import { Panel, PanelBody, PanelHeader } from '@/ui/components/Panel';
import { Button } from '@/ui/components/Button';
import { PropertyEditor } from '@/ui/properties/PropertyEditor';
import { TrendGraph } from './TrendGraph';
import { AnalogStatusMonitor } from './AnalogStatusMonitor';
import { HMIPropertiesPanel } from '@/ui/editor/hmi/HMIPropertiesPanel';
import type { DeviceId } from '@/core/types';
import styles from './PropertiesPanel.module.css';

import { useLadderEditorStore } from '@/ui/editor/ladder/store/ladderEditorStore';
import type { LadderElement } from '@/ladder/model/LadderElement';

const isDeviceType = (type: SelectableObjectType): type is DeviceType =>
  (DEVICE_TYPES as readonly string[]).includes(type);

/** Generic properties inspector: driven by the selection and the device's property schema. */
export function PropertiesPanel() {
  const t = useT();
  const selectedId = useUiStore((state) => state.selectedObjectId);
  const selectedType = useUiStore((state) => state.selectedObjectType);
  const isSimRunning = useSimulationStore((state) => state.snapshot.state !== 'STOPPED');
  const devices = useProjectStore((state) => state.project?.scene?.devices ?? []);
  const pipes = useProjectStore((state) => state.project?.scene?.pipes ?? []);
  const updatePipe = useProjectStore((state) => state.updatePipe);
  const removePipe = useProjectStore((state) => state.removePipe);

  const [manualAddressMap, setManualAddressMap] = useState<Record<string, boolean>>({});

  const hasSelection = !isSimRunning && selectedId !== null && selectedType !== null;
  const definition =
    hasSelection && isDeviceType(selectedType) ? deviceRegistry.get(selectedType) : undefined;
  const device = devices.find((candidate) => candidate.id === selectedId);
  
  const program = useProjectStore((state) => state.project?.plc?.program ?? { networks: [] });
  let ladderElement: LadderElement | undefined;
  let ladderNetworkId: string | undefined;
  if (selectedType === 'ladderElement' && selectedId && program?.networks) {
    for (const net of program.networks) {
      if (net?.elements && net.elements[selectedId]) {
        ladderElement = net.elements[selectedId];
        ladderNetworkId = net.id;
        break;
      }
    }
  }

  const values = device?.props ?? definition?.defaultProps ?? {};
  const typeLabel = definition
    ? t(definition.meta.nameKey)
    : selectedType === 'ladderElement'
      ? (() => {
          if (!ladderElement) return 'Element';
          switch (ladderElement.type) {
            case 'CMP_EQ_I': return '==I (Eşit / Equal)';
            case 'CMP_NE_I': return '<>I (Eşit Değil / Not Equal)';
            case 'CMP_GT_I': return '>I (Büyük / Greater Than)';
            case 'CMP_LT_I': return '<I (Küçük / Less Than)';
            case 'CMP_GE_I': return '>=I (Büyük Eşit / GE)';
            case 'CMP_LE_I': return '<=I (Küçük Eşit / LE)';
            case 'CMP_EQ_R': return '==R (Float Eşit / Equal)';
            case 'CMP_NE_R': return '<>R (Float Eşit Değil / Not Equal)';
            case 'CMP_GT_R': return '>R (Float Büyük / Greater Than)';
            case 'CMP_LT_R': return '<R (Float Küçük / Less Than)';
            case 'CMP_GE_R': return '>=R (Float Büyük Eşit / GE)';
            case 'CMP_LE_R': return '<=R (Float Küçük Eşit / LE)';
            case 'ADD_I': return 'ADD_I (Toplama / Add)';
            case 'SUB_I': return 'SUB_I (Çıkarma / Subtract)';
            case 'MUL_I': return 'MUL_I (Çarpma / Multiply)';
            case 'DIV_I': return 'DIV_I (Bölme / Divide)';
            case 'ADD_R': return 'ADD_R (Float Toplama / Add)';
            case 'SUB_R': return 'SUB_R (Float Çıkarma / Subtract)';
            case 'MUL_R': return 'MUL_R (Float Çarpma / Multiply)';
            case 'DIV_R': return 'DIV_R (Float Bölme / Divide)';
            case 'MOV_W': return 'MOV_W (16-bit Word Taşıma)';
            case 'MOV_R': return 'MOV_R (32-bit Real Taşıma)';
            case 'NO_CONTACT': return 'Normalde Açık Kontak (NO)';
            case 'NC_CONTACT': return 'Normalde Kapalı Kontak (NC)';
            case 'P_CONTACT': return 'Yükselen Kenar Kontağı (P)';
            case 'N_CONTACT': return 'Düşen Kenar Kontağı (N)';
            case 'NOT_CONTACT': return 'Mantıksal Değil / İnverter (NOT)';
            case 'COIL': return 'Çıkış Bobini (Coil)';
            case 'INVERT_COIL': return 'Ters Çıkış Bobini (/)';
            case 'SET_COIL': return 'Set Bobini (S)';
            case 'RESET_COIL': return 'Reset Bobini (R)';
            case 'SR': return 'SR Flip-Flop (Set Öncelikli)';
            case 'RS': return 'RS Flip-Flop (Reset Öncelikli)';
            case 'TON': return 'Zamanlayıcı (TON - On Delay)';
            case 'TOF': return 'Zamanlayıcı (TOF - Off Delay)';
            case 'TP': return 'Darbe Zamanlayıcı (TP)';
            case 'TONR': return 'Kalıcı Zamanlayıcı (TONR)';
            case 'CTU': return 'İleri Sayıcı (CTU)';
            case 'CTD': return 'Geri Sayıcı (CTD)';
            case 'CTUD': return 'İleri/Geri Sayıcı (CTUD)';
            case 'SCALE': return 'Analog Ölçekleme (SCALE)';
            case 'IN_RANGE': return 'Aralık Denetimi (IN_RANGE)';
            case 'JMP': return 'Koşullu Atlama (JMP)';
            case 'LBL': return 'Atlama Hedef Etiketi (LBL)';
            case 'H_LINE': return 'Yatay Hat';
            case 'V_LINE': return 'Dikey Hat';
            default: return 'Element';
          }
        })()
      : selectedType === 'network'
        ? t('properties.type.network')
        : selectedType === 'wire'
          ? t('properties.type.wire')
          : selectedType === 'pipe'
            ? 'Boru Hattı'
            : '';

  return (
    <Panel label={t('properties.label')} className={styles.panel}>
      <PanelHeader title={t('properties.title')} icon="settings" />
      <PanelBody>
        {hasSelection ? (
          selectedType === 'hmiComponent' ? (
            <HMIPropertiesPanel />
          ) : (
            <div className={styles.content}>
              <section aria-label={t('properties.general')} className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('properties.general')}</h3>
              <dl className={styles.facts}>
                <dt>{t('properties.field.type')}</dt>
                <dd>{typeLabel}</dd>
                <dt>{t('properties.field.id')}</dt>
                <dd className={styles.mono}>{selectedId}</dd>
                {device && (
                  <>
                    <dt style={{ alignSelf: 'center' }}>{t('properties.field.name')}</dt>
                    <dd style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        type="text"
                        value={device.name ?? ''}
                        placeholder={typeLabel || 'Komponent İsmi'}
                        onChange={(e) => {
                          const val = e.target.value;
                          const newDevices = devices.map((d) => (d.id === selectedId ? { ...d, name: val } : d));
                          useProjectStore.getState().setDevices(newDevices);
                        }}
                        style={{
                          width: '100%',
                          padding: '5px 8px',
                          background: 'rgba(15, 23, 42, 0.7)',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          borderRadius: 4,
                          color: '#f8fafc',
                          fontSize: 12,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => {
                            const newDevices = devices.map((d) => {
                              if (d.id === selectedId) {
                                const cur = d.props['showNameLabel'] !== false;
                                return { ...d, props: { ...d.props, showNameLabel: !cur } };
                              }
                              return d;
                            });
                            useProjectStore.getState().setDevices(newDevices);
                          }}
                          style={{
                            background: device.props['showNameLabel'] === false ? 'rgba(239, 68, 68, 0.12)' : 'rgba(245, 158, 11, 0.15)',
                            border: `1px solid ${device.props['showNameLabel'] === false ? 'rgba(239, 68, 68, 0.35)' : 'rgba(245, 158, 11, 0.4)'}`,
                            color: device.props['showNameLabel'] === false ? '#f87171' : '#fbbf24',
                            fontSize: 11,
                            cursor: 'pointer',
                            padding: '2px 8px',
                            borderRadius: 4,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                          title="Sahne üzerindeki komponent isim etiketini göster veya gizle"
                        >
                          {device.props['showNameLabel'] === false ? '👁️ İsmi Göster' : '👁️‍🗨️ İsmi Gizle'}
                        </button>
                        {Boolean(device.props['nameOffsetX'] || device.props['nameOffsetY']) && (
                          <button
                            type="button"
                            onClick={() => {
                              const newDevices = devices.map((d) => {
                                if (d.id === selectedId) {
                                  const nextProps = { ...d.props };
                                  delete nextProps['nameOffsetX'];
                                  delete nextProps['nameOffsetY'];
                                  return { ...d, props: nextProps };
                                }
                                return d;
                              });
                              useProjectStore.getState().setDevices(newDevices);
                            }}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              color: '#f87171',
                              fontSize: 11,
                              cursor: 'pointer',
                              padding: '2px 6px',
                              borderRadius: 4,
                            }}
                            title="İsim etiketini varsayılan konumuna sıfırla"
                          >
                            ↺ Sıfırla
                          </button>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '4px 8px',
                          background: 'rgba(255, 255, 255, 0.02)',
                          borderRadius: 4,
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                        }}
                      >
                        <span style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span>🏷️</span> {t('properties.showNameLabel')}
                        </span>
                        <input
                          type="checkbox"
                          checked={device?.props['showNameLabel'] !== false}
                          onChange={(e) => {
                            const newDevices = devices.map((d) => {
                              if (d.id === selectedId) {
                                return { ...d, props: { ...d.props, showNameLabel: e.target.checked } };
                              }
                              return d;
                            });
                            useProjectStore.getState().setDevices(newDevices);
                          }}
                          style={{ cursor: 'pointer', accentColor: '#f59e0b', width: 15, height: 15 }}
                        />
                      </div>
                    </dd>
                  </>
                )}
                {selectedType === 'pipe' && (() => {
                  const pipes = useProjectStore.getState().project.scene.pipes || [];
                  const pipe = pipes.find(p => p.id === selectedId);
                  if (!pipe) return null;
                  const fromDev = devices.find(d => d.id === pipe.from.deviceId);
                  const toDev = devices.find(d => d.id === pipe.to.deviceId);
                  return (
                    <>
                      <dt>Başlangıç</dt>
                      <dd>{fromDev ? `${fromDev.name} (${pipe.from.portType === 'outlet' ? 'Çıkış' : 'Giriş'})` : pipe.from.deviceId}</dd>
                      <dt>Bitiş</dt>
                      <dd>{toDev ? `${toDev.name} (${pipe.to.portType === 'inlet' ? 'Giriş' : 'Çıkış'})` : pipe.to.deviceId}</dd>
                    </>
                  );
                })()}
              </dl>
              {selectedType === 'ladderElement' && ladderElement && (
                <div style={{ marginTop: 12, padding: 8, backgroundColor: 'var(--color-bg-base)', borderLeft: '3px solid var(--color-primary)', fontSize: '0.85rem' }}>
                  {(() => {
                    switch (ladderElement.type) {
                      case 'CMP_EQ_I': return '==I\nİki tamsayıyı karşılaştırır.\nIN1 === IN2 ise TRUE üretir.';
                      case 'CMP_NE_I': return '<>I\nİki tamsayıyı karşılaştırır.\nIN1 !== IN2 ise TRUE üretir.';
                      case 'CMP_GT_I': return '>I\nİki tamsayıyı karşılaştırır.\nIN1 > IN2 ise TRUE üretir.';
                      case 'CMP_LT_I': return '<I\nİki tamsayıyı karşılaştırır.\nIN1 < IN2 ise TRUE üretir.';
                      case 'CMP_GE_I': return '>=I\nİki tamsayıyı karşılaştırır.\nIN1 >= IN2 ise TRUE üretir.';
                      case 'CMP_LE_I': return '<=I\nİki tamsayıyı karşılaştırır.\nIN1 <= IN2 ise TRUE üretir.';
                      case 'CMP_EQ_R': return '==R\nİki reel (float) sayıyı karşılaştırır.\nIN1 === IN2 ise TRUE üretir.';
                      case 'CMP_NE_R': return '<>R\nİki reel (float) sayıyı karşılaştırır.\nIN1 !== IN2 ise TRUE üretir.';
                      case 'CMP_GT_R': return '>R\nİki reel (float) sayıyı karşılaştırır.\nIN1 > IN2 ise TRUE üretir.';
                      case 'CMP_LT_R': return '<R\nİki reel (float) sayıyı karşılaştırır.\nIN1 < IN2 ise TRUE üretir.';
                      case 'CMP_GE_R': return '>=R\nİki reel (float) sayıyı karşılaştırır.\nIN1 >= IN2 ise TRUE üretir.';
                      case 'CMP_LE_R': return '<=R\nİki reel (float) sayıyı karşılaştırır.\nIN1 <= IN2 ise TRUE üretir.';
                      case 'ADD_I': return 'ADD_I\nTamsayı toplama işlemi yapar.\nIN1 + IN2 sonucunu OUT adresine yazar.';
                      case 'SUB_I': return 'SUB_I\nTamsayı çıkarma işlemi yapar.\nIN1 - IN2 sonucunu OUT adresine yazar.';
                      case 'MUL_I': return 'MUL_I\nTamsayı çarpma işlemi yapar.\nIN1 * IN2 sonucunu OUT adresine yazar.';
                      case 'DIV_I': return 'DIV_I\nTamsayı bölme işlemi yapar.\nIN1 / IN2 sonucunu OUT adresine yazar.';
                      case 'ADD_R': return 'ADD_R\nReel (float) toplama işlemi yapar.\nIN1 + IN2 sonucunu OUT adresine yazar.';
                      case 'SUB_R': return 'SUB_R\nReel (float) çıkarma işlemi yapar.\nIN1 - IN2 sonucunu OUT adresine yazar.';
                      case 'MUL_R': return 'MUL_R\nReel (float) çarpma işlemi yapar.\nIN1 * IN2 sonucunu OUT adresine yazar.';
                      case 'DIV_R': return 'DIV_R\nReel (float) bölme işlemi yapar.\nIN1 / IN2 sonucunu OUT adresine yazar.';
                      case 'MOV_W': return 'MOV_W\n16-bit tamsayı taşıma işlemi yapar.\nIN değerini okuyup OUT adresine kopyalar.';
                      case 'MOV_R': return 'MOV_R\n32-bit reel (float) taşıma işlemi yapar.\nIN değerini okuyup OUT adresine kopyalar.';
                      case 'NO_CONTACT': return 'Normalde Açık Kontak (NO)\nİlgili bit 1 (TRUE) olduğunda akımı geçirir.';
                      case 'NC_CONTACT': return 'Normalde Kapalı Kontak (NC)\nİlgili bit 0 (FALSE) olduğunda akımı geçirir.';
                      case 'P_CONTACT': return 'Yükselen Kenar Kontağı (P)\nİlgili bit FALSE->TRUE geçişi yaptığında bir tarama süresince TRUE üretir.';
                      case 'N_CONTACT': return 'Düşen Kenar Kontağı (N)\nİlgili bit TRUE->FALSE geçişi yaptığında bir tarama süresince TRUE üretir.';
                      case 'NOT_CONTACT': return 'Mantıksal Değil / İnverter (NOT)\nKendisine gelen mantık akışını tersine çevirir.';
                      case 'COIL': return 'Çıkış Bobini (Coil)\nKendisine gelen mantık akışını doğrudan hedef bite yazar.';
                      case 'INVERT_COIL': return 'Ters Çıkış Bobini (/)\nKendisine gelen mantık akışının tersini hedef bite yazar.';
                      case 'SET_COIL': return 'Set Bobini (S)\nGiriş TRUE olduğunda hedef biti 1 yapar ve kilitler.';
                      case 'RESET_COIL': return 'Reset Bobini (R)\nGiriş TRUE olduğunda hedef biti 0 yapar.';
                      case 'SR': return 'SR Flip-Flop (Set Öncelikli)\nS ve R1 aynı anda aktifse Set durumu baskındır.';
                      case 'RS': return 'RS Flip-Flop (Reset Öncelikli)\nS ve R aynı anda aktifse Reset durumu baskındır.';
                      case 'TON': return 'TON (On Delay Timer)\nGiriş sinyali TRUE olduktan sonra Preset (PT) süresi kadar bekler, ardından çıkış verir.';
                      case 'TOF': return 'TOF (Off Delay Timer)\nGiriş sinyali FALSE olduktan sonra Preset (PT) süresi kadar bekler, ardından çıkışı keser.';
                      case 'TP': return 'TP (Pulse Timer)\nGiriş sinyali TRUE olduğunda Preset (PT) süresi kadar çıkış verir, sonra kesilir.';
                      case 'TONR': return 'TONR (Retentive On Delay Timer)\nGiriş TRUE iken süreyi sayar. Giriş kesilirse kaldığı süreyi korur. R girişi ile sıfırlanır.';
                      case 'CTU': return 'CTU (Count Up)\nGiriş sinyali her TRUE olduğunda sayacı 1 artırır.';
                      case 'CTD': return 'CTD (Count Down)\nGiriş sinyali her TRUE olduğunda sayacı 1 azaltır.';
                      case 'CTUD': return 'CTUD (Count Up/Down)\nİleri ve geri sayma işlemlerini birlikte destekleyen sayıcı bloğudur.';
                      case 'SCALE': return 'SCALE (Doğrusal Ölçekleme)\nGiriş değerini [IN_MIN..IN_MAX] aralığından [OUT_MIN..OUT_MAX] aralığına dönüştürür.';
                      case 'IN_RANGE': return 'IN_RANGE (Aralık Denetimi)\nGiriş değeri [MIN..MAX] sınırları arasındaysa akımı geçirir.';
                      case 'JMP': return 'JMP (Koşullu Atlama)\nGiriş akımı TRUE olduğunda belirtilen LBL etiketine atlar.';
                      case 'LBL': return 'LBL (Atlama Etiketi)\nJMP komutunun hedef alacağı satır etiketidir.';
                      default: return null;
                    }
                  })()?.split('\n').map((line, i) => <div key={i}>{line}</div>)}
                </div>
              )}
            </section>
            {selectedType === 'ladderElement' && ladderElement && (() => {
              const elType = ladderElement.type;
              const isCompare = ['CMP_EQ_I', 'CMP_NE_I', 'CMP_GT_I', 'CMP_LT_I', 'CMP_GE_I', 'CMP_LE_I'].includes(elType);
              const isCompareReal = ['CMP_EQ_R', 'CMP_NE_R', 'CMP_GT_R', 'CMP_LT_R', 'CMP_GE_R', 'CMP_LE_R'].includes(elType);
              const isMath = ['ADD_I', 'SUB_I', 'MUL_I', 'DIV_I'].includes(elType);
              const isMathReal = ['ADD_R', 'SUB_R', 'MUL_R', 'DIV_R'].includes(elType);
              const isMove = ['MOV_W', 'MOV_R'].includes(elType);
              const isTimer = ['TON', 'TOF', 'TP', 'TONR'].includes(elType);
              const isCounter = ['CTU', 'CTD', 'CTUD'].includes(elType);
              const isContact = ['NO_CONTACT', 'NC_CONTACT', 'P_CONTACT', 'N_CONTACT', 'NOT_CONTACT'].includes(elType);
              const isCoil = ['COIL', 'SET_COIL', 'RESET_COIL', 'INVERT_COIL'].includes(elType);
              const isFlipFlop = ['SR', 'RS'].includes(elType);
              const isScale = elType === 'SCALE';
              const isInRange = elType === 'IN_RANGE';
              const isJumpOrLabel = ['JMP', 'LBL'].includes(elType);

              const showAddress = (isContact && elType !== 'NOT_CONTACT') || isCoil || isTimer || isCounter || isFlipFlop || 'address' in ladderElement;
              const showPresetTime = isTimer || 'presetTime' in ladderElement;
              const showPresetValue = isCounter || 'presetValue' in ladderElement;
              const showIn1 = isCompare || isCompareReal || isMath || isMathReal || 'in1' in ladderElement;
              const showIn2 = isCompare || isCompareReal || isMath || isMathReal || 'in2' in ladderElement;
              const showIn = isMove || isScale || isInRange || 'in' in ladderElement;
              const showOut = isMath || isMathReal || isMove || isScale || 'out' in ladderElement;
              const showMinMax = isInRange || ('min' in ladderElement && 'max' in ladderElement);
              const showScaleParams = isScale || 'inMin' in ladderElement;
              const showLabel = isJumpOrLabel || 'label' in ladderElement;

              const updateElement = (updates: Record<string, any>) => {
                if (!ladderElement || !ladderNetworkId) return;
                const newProgram = {
                  ...program,
                  networks: program.networks.map(n => n.id === ladderNetworkId ? {
                    ...n,
                    elements: {
                      ...n.elements,
                      [ladderElement.id]: {
                        ...ladderElement,
                        ...updates,
                      } as any
                    }
                  } : n)
                };
                useLadderEditorStore.getState().executeCommand(newProgram);
              };

              let typeOptions: { value: string; labelKey: any }[] | null = null;
              if (isCompare) {
                typeOptions = [
                  { value: 'CMP_EQ_I', labelKey: '==I (Eşit / Equal)' },
                  { value: 'CMP_NE_I', labelKey: '<>I (Eşit Değil / Not Equal)' },
                  { value: 'CMP_GT_I', labelKey: '>I (Büyük / Greater Than)' },
                  { value: 'CMP_LT_I', labelKey: '<I (Küçük / Less Than)' },
                  { value: 'CMP_GE_I', labelKey: '>=I (Büyük Eşit / Greater Equal)' },
                  { value: 'CMP_LE_I', labelKey: '<=I (Küçük Eşit / Less Equal)' },
                ];
              } else if (isCompareReal) {
                typeOptions = [
                  { value: 'CMP_EQ_R', labelKey: '==R (Float Eşit / Equal)' },
                  { value: 'CMP_NE_R', labelKey: '<>R (Float Eşit Değil / Not Equal)' },
                  { value: 'CMP_GT_R', labelKey: '>R (Float Büyük / Greater Than)' },
                  { value: 'CMP_LT_R', labelKey: '<R (Float Küçük / Less Than)' },
                  { value: 'CMP_GE_R', labelKey: '>=R (Float Büyük Eşit / GE)' },
                  { value: 'CMP_LE_R', labelKey: '<=R (Float Küçük Eşit / LE)' },
                ];
              } else if (isMath) {
                typeOptions = [
                  { value: 'ADD_I', labelKey: 'ADD_I (Toplama / Add)' },
                  { value: 'SUB_I', labelKey: 'SUB_I (Çıkarma / Subtract)' },
                  { value: 'MUL_I', labelKey: 'MUL_I (Çarpma / Multiply)' },
                  { value: 'DIV_I', labelKey: 'DIV_I (Bölme / Divide)' },
                ];
              } else if (isMathReal) {
                typeOptions = [
                  { value: 'ADD_R', labelKey: 'ADD_R (Float Toplama)' },
                  { value: 'SUB_R', labelKey: 'SUB_R (Float Çıkarma)' },
                  { value: 'MUL_R', labelKey: 'MUL_R (Float Çarpma)' },
                  { value: 'DIV_R', labelKey: 'DIV_R (Float Bölme)' },
                ];
              } else if (isMove) {
                typeOptions = [
                  { value: 'MOV_W', labelKey: 'MOV_W (16-bit Word)' },
                  { value: 'MOV_R', labelKey: 'MOV_R (32-bit Real)' },
                ];
              } else if (isTimer) {
                typeOptions = [
                  { value: 'TON', labelKey: 'TON (On Delay)' },
                  { value: 'TOF', labelKey: 'TOF (Off Delay)' },
                  { value: 'TP', labelKey: 'TP (Pulse)' },
                  { value: 'TONR', labelKey: 'TONR (Kalıcı On Delay / Retentive)' },
                ];
              } else if (isCounter) {
                typeOptions = [
                  { value: 'CTU', labelKey: 'CTU (Count Up)' },
                  { value: 'CTD', labelKey: 'CTD (Count Down)' },
                  { value: 'CTUD', labelKey: 'CTUD (Count Up/Down)' },
                ];
              } else if (isContact) {
                typeOptions = [
                  { value: 'NO_CONTACT', labelKey: 'NO (Normalde Açık Kontak)' },
                  { value: 'NC_CONTACT', labelKey: 'NC (Normalde Kapalı Kontak)' },
                  { value: 'P_CONTACT', labelKey: 'P (Yükselen Kenar Kontağı)' },
                  { value: 'N_CONTACT', labelKey: 'N (Düşen Kenar Kontağı)' },
                  { value: 'NOT_CONTACT', labelKey: 'NOT (RLO Evirici)' },
                ];
              } else if (isCoil) {
                typeOptions = [
                  { value: 'COIL', labelKey: 'COIL (Normal Bobin)' },
                  { value: 'INVERT_COIL', labelKey: 'INVERT_COIL (Ters Bobin)' },
                  { value: 'SET_COIL', labelKey: 'SET_COIL (Set Bobini)' },
                  { value: 'RESET_COIL', labelKey: 'RESET_COIL (Reset Bobini)' },
                ];
              } else if (isFlipFlop) {
                typeOptions = [
                  { value: 'SR', labelKey: 'SR (Set Dominant Flip-Flop)' },
                  { value: 'RS', labelKey: 'RS (Reset Dominant Flip-Flop)' },
                ];
              } else if (isJumpOrLabel) {
                typeOptions = [
                  { value: 'JMP', labelKey: 'JMP (Koşullu Atlama / Jump)' },
                  { value: 'LBL', labelKey: 'LBL (Hedef Etiket / Label)' },
                ];
              }

              return (
                <div className={styles.editors}>
                  {typeOptions && (
                    <PropertyEditor
                      definition={{
                        key: 'type',
                        labelKey: 'properties.ladder.opType' as any,
                        kind: 'select',
                        options: typeOptions
                      }}
                      value={elType}
                      disabled={false}
                      onChange={(_, val) => updateElement({ type: val })}
                    />
                  )}
                  {showAddress && (
                    <PropertyEditor
                      definition={{ key: 'address', labelKey: 'properties.ladder.address' as any, kind: 'text' }}
                      value={(ladderElement as any).address ?? ''}
                      disabled={false}
                      onChange={(_, val) => updateElement({ address: val })}
                    />
                  )}
                  {showLabel && (
                    <PropertyEditor
                      definition={{ key: 'label', labelKey: 'properties.ladder.label' as any, kind: 'text' }}
                      value={(ladderElement as any).label ?? ''}
                      disabled={false}
                      onChange={(_, val) => updateElement({ label: val })}
                    />
                  )}
                  {showPresetTime && (
                    <PropertyEditor
                      definition={{ key: 'presetTime', labelKey: 'properties.ladder.presetTime' as any, kind: 'text' }}
                      value={(ladderElement as any).presetTime ?? ''}
                      disabled={false}
                      onChange={(_, val) => updateElement({ presetTime: val })}
                    />
                  )}
                  {showPresetValue && (
                    <PropertyEditor
                      definition={{ key: 'presetValue', labelKey: 'properties.ladder.presetValue' as any, kind: 'text' }}
                      value={(ladderElement as any).presetValue ?? ''}
                      disabled={false}
                      onChange={(_, val) => updateElement({ presetValue: val })}
                    />
                  )}
                  {showIn1 && (
                    <PropertyEditor
                      definition={{ key: 'in1', labelKey: 'properties.ladder.in1' as any, kind: 'text' }}
                      value={(ladderElement as any).in1 ?? ''}
                      disabled={false}
                      onChange={(_, val) => updateElement({ in1: val })}
                    />
                  )}
                  {showIn2 && (
                    <PropertyEditor
                      definition={{ key: 'in2', labelKey: 'properties.ladder.in2' as any, kind: 'text' }}
                      value={(ladderElement as any).in2 ?? ''}
                      disabled={false}
                      onChange={(_, val) => updateElement({ in2: val })}
                    />
                  )}
                  {showIn && (
                    <PropertyEditor
                      definition={{ key: 'in', labelKey: 'properties.ladder.in' as any, kind: 'text' }}
                      value={(ladderElement as any).in ?? ''}
                      disabled={false}
                      onChange={(_, val) => updateElement({ in: val })}
                    />
                  )}
                  {showMinMax && (
                    <>
                      <PropertyEditor
                        definition={{ key: 'min', labelKey: 'properties.ladder.min' as any, kind: 'text' }}
                        value={(ladderElement as any).min ?? ''}
                        disabled={false}
                        onChange={(_, val) => updateElement({ min: val })}
                      />
                      <PropertyEditor
                        definition={{ key: 'max', labelKey: 'properties.ladder.max' as any, kind: 'text' }}
                        value={(ladderElement as any).max ?? ''}
                        disabled={false}
                        onChange={(_, val) => updateElement({ max: val })}
                      />
                    </>
                  )}
                  {showScaleParams && (
                    <>
                      <PropertyEditor
                        definition={{ key: 'inMin', labelKey: 'properties.ladder.inMin' as any, kind: 'text' }}
                        value={(ladderElement as any).inMin ?? ''}
                        disabled={false}
                        onChange={(_, val) => updateElement({ inMin: val })}
                      />
                      <PropertyEditor
                        definition={{ key: 'inMax', labelKey: 'properties.ladder.inMax' as any, kind: 'text' }}
                        value={(ladderElement as any).inMax ?? ''}
                        disabled={false}
                        onChange={(_, val) => updateElement({ inMax: val })}
                      />
                      <PropertyEditor
                        definition={{ key: 'outMin', labelKey: 'properties.ladder.outMin' as any, kind: 'text' }}
                        value={(ladderElement as any).outMin ?? ''}
                        disabled={false}
                        onChange={(_, val) => updateElement({ outMin: val })}
                      />
                      <PropertyEditor
                        definition={{ key: 'outMax', labelKey: 'properties.ladder.outMax' as any, kind: 'text' }}
                        value={(ladderElement as any).outMax ?? ''}
                        disabled={false}
                        onChange={(_, val) => updateElement({ outMax: val })}
                      />
                    </>
                  )}
                  {showOut && (
                    <PropertyEditor
                      definition={{ key: 'out', labelKey: 'properties.ladder.out' as any, kind: 'text' }}
                      value={(ladderElement as any).out ?? ''}
                      disabled={false}
                      onChange={(_, val) => updateElement({ out: val })}
                    />
                  )}
                </div>
              );
            })()}
            {definition && definition.propertySchema.length > 0 ? (
              (() => {
                const effectiveSchema = [...definition.propertySchema];
                if (device?.type === 'motor' && device.props['variant'] === 'pump_motor') {
                  if (!effectiveSchema.some((p) => p.key === 'targetId')) {
                    effectiveSchema.push(
                      { key: 'nominalFlowRate', labelKey: 'properties.nominalFlowRate' as TranslationKey, kind: 'text' },
                      { key: 'sourceId', labelKey: 'properties.sourceId' as TranslationKey, kind: 'text' },
                      { key: 'targetId', labelKey: 'properties.targetId' as TranslationKey, kind: 'text' }
                    );
                  }
                }
                return (
                  <div className={styles.editors}>
                    {effectiveSchema.map((property) => {
                  let activeProperty = property;
                  
                  if (property.key === 'address') {
                    const ladderAddresses = new Set<string>();
                    for (const net of program?.networks ?? []) {
                      for (const el of Object.values(net?.elements ?? {})) {
                        if ('address' in el && typeof el.address === 'string' && el.address) ladderAddresses.add(el.address.trim());
                        if ('in' in el && typeof el.in === 'string' && el.in) ladderAddresses.add(el.in.trim());
                        if ('out' in el && typeof el.out === 'string' && el.out) ladderAddresses.add(el.out.trim());
                        if ('in1' in el && typeof (el as any).in1 === 'string' && (el as any).in1) {
                          const v = (el as any).in1.trim();
                          if (v && isNaN(Number(v))) ladderAddresses.add(v);
                        }
                        if ('in2' in el && typeof (el as any).in2 === 'string' && (el as any).in2) {
                          const v = (el as any).in2.trim();
                          if (v && isNaN(Number(v))) ladderAddresses.add(v);
                        }
                        if ('min' in el && typeof (el as any).min === 'string' && (el as any).min) {
                          const v = (el as any).min.trim();
                          if (v && isNaN(Number(v))) ladderAddresses.add(v);
                        }
                        if ('max' in el && typeof (el as any).max === 'string' && (el as any).max) {
                          const v = (el as any).max.trim();
                          if (v && isNaN(Number(v))) ladderAddresses.add(v);
                        }
                        if ('inMin' in el && typeof (el as any).inMin === 'string' && (el as any).inMin) {
                          const v = (el as any).inMin.trim();
                          if (v && isNaN(Number(v))) ladderAddresses.add(v);
                        }
                        if ('inMax' in el && typeof (el as any).inMax === 'string' && (el as any).inMax) {
                          const v = (el as any).inMax.trim();
                          if (v && isNaN(Number(v))) ladderAddresses.add(v);
                        }
                      }
                    }

                    const addresses = new Set<string>();
                    // 1. Ladder used addresses first
                    ladderAddresses.forEach((a) => addresses.add(a));

                    // 2. Currently selected address
                    const curAddr = values['address'];
                    if (curAddr && typeof curAddr === 'string' && curAddr.trim()) addresses.add(curAddr.trim());

                    // 3. Recommended standard addresses for this device
                    if (device?.type === 'level-sensor') {
                      ['MW0', 'MW2', 'MW4', 'MW6', 'MW8', 'MW10', 'MW12', 'MW20', 'IW0', 'IW2', 'IW4', 'IW6', 'MD0', 'MD4', 'MD8'].forEach((a) => addresses.add(a));
                    } else if (device && ['button', 'switch', 'emergency-stop', 'sensor', 'jam-sensor'].includes(device.type)) {
                      ['I0.0', 'I0.1', 'I0.2', 'I0.3', 'I0.4', 'I0.5', 'I0.6', 'I0.7', 'I1.0', 'I1.1', 'M0.0', 'M0.1'].forEach((a) => addresses.add(a));
                    } else if (device && ['lamp', 'buzzer', 'motor', 'fan', 'pump', 'valve'].includes(device.type)) {
                      ['Q0.0', 'Q0.1', 'Q0.2', 'Q0.3', 'Q0.4', 'Q0.5', 'Q0.6', 'Q0.7', 'Q1.0', 'Q1.1', 'M0.0', 'M0.1'].forEach((a) => addresses.add(a));
                    }

                    const isManual = Boolean(manualAddressMap[selectedId]);
                    const options = Array.from(addresses).map((a) => ({ value: a, labelKey: a as any }));
                    options.unshift({ value: '', labelKey: '- (Atanmamış)' as any });

                    activeProperty = {
                      key: 'address',
                      labelKey: 'properties.general' as any,
                      kind: isManual ? 'text' : 'select',
                      options,
                    };
                  }

                  if (['sourceId', 'targetId', 'tankId'].includes(property.key)) {
                    let targetDevices = devices.filter(d => d.type === 'tank');
                    
                    if (device?.type === 'pid-controller') {
                       if (property.key === 'sourceId') {
                          targetDevices = devices.filter(d => d.type.includes('sensor'));
                       } else if (property.key === 'targetId') {
                          targetDevices = devices.filter(d => ['pump', 'valve', 'motor'].includes(d.type));
                       }
                    }

                    const options = targetDevices.map(d => ({ value: d.id as string, labelKey: `${d.name} (${d.id})` as any }));
                    options.unshift({ value: '', labelKey: '-' as any });
                    activeProperty = {
                      ...activeProperty,
                      kind: 'select',
                      options
                    };
                  }

                  if (property.key === 'motorId') {
                    const motors = devices.filter(d => d.type === 'motor');
                    const options = motors.map(m => ({ value: m.id as string, labelKey: `${m.name} (${m.id})` as any }));
                    options.unshift({ value: '', labelKey: '-' as any });
                    activeProperty = {
                      ...activeProperty,
                      kind: 'select',
                      options
                    };
                  }

                  if (property.key === 'conveyorId' || property.key === 'outputConnection') {
                    const conveyors = devices.filter(d => d.type === 'conveyor');
                    const options = conveyors.map(c => ({ value: c.id as string, labelKey: `${c.name} (${c.id})` as any }));
                    options.unshift({ value: '', labelKey: '-' as any });
                    activeProperty = {
                      ...activeProperty,
                      kind: 'select',
                      options
                    };
                  }

                  if (property.key === 'tankId') {
                    const tanks = devices.filter(d => d.type === 'tank');
                    const options = tanks.map(t => ({ value: t.id as string, labelKey: `${t.name} (${t.id})` as any }));
                    options.unshift({ value: '', labelKey: '-' as any });
                    activeProperty = {
                      ...activeProperty,
                      kind: 'select',
                      options
                    };
                  }

                  const isAddressProp = property.key === 'address';
                  const isManual = Boolean(manualAddressMap[selectedId]);

                  return (
                    <div key={`${selectedId}-${activeProperty.key}`} style={{ marginBottom: 6 }}>
                      {isAddressProp && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2, marginTop: -2, gap: 6, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => {
                              const newDevices = devices.map((d) => {
                                if (d.id === selectedId) {
                                  const curShow = d.props['showLabel'] !== false;
                                  return { ...d, props: { ...d.props, showLabel: !curShow } };
                                }
                                return d;
                              });
                              useProjectStore.getState().setDevices(newDevices);
                            }}
                            style={{
                              background: device?.props['showLabel'] === false ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.12)',
                              border: `1px solid ${device?.props['showLabel'] === false ? 'rgba(239, 68, 68, 0.35)' : 'rgba(34, 197, 94, 0.35)'}`,
                              color: device?.props['showLabel'] === false ? '#f87171' : '#4ade80',
                              fontSize: '11px',
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                            title="Sahne üzerindeki PLC adres etiketini göster veya gizle"
                          >
                            {device?.props['showLabel'] === false ? '👁️ Etiketi Göster' : '👁️‍🗨️ Etiketi Gizle'}
                          </button>
                          {Boolean(device?.props['labelOffsetX'] || device?.props['labelOffsetY']) && (
                            <button
                              type="button"
                              onClick={() => {
                                const newDevices = devices.map((d) => {
                                  if (d.id === selectedId) {
                                    const nextProps = { ...d.props };
                                    delete nextProps['labelOffsetX'];
                                    delete nextProps['labelOffsetY'];
                                    return { ...d, props: nextProps };
                                  }
                                  return d;
                                });
                                useProjectStore.getState().setDevices(newDevices);
                              }}
                              style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                color: '#f87171',
                                fontSize: '11px',
                                cursor: 'pointer',
                                padding: '2px 8px',
                                borderRadius: '4px',
                              }}
                              title="Taşınan etiket konumunu varsayılana sıfırla"
                            >
                              ↺ Etiketi Sıfırla
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              setManualAddressMap((prev) => ({
                                ...prev,
                                [selectedId]: !prev[selectedId],
                              }))
                            }
                            style={{
                              background: 'rgba(56, 189, 248, 0.12)',
                              border: '1px solid rgba(56, 189, 248, 0.35)',
                              color: 'var(--color-primary, #38bdf8)',
                              fontSize: '11px',
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            {isManual ? '📋 Listeden Seç' : '✏️ Manuel Adres Yaz'}
                          </button>
                        </div>
                      )}
                      <PropertyEditor
                        definition={activeProperty}
                        value={values[activeProperty.key] ?? ''}
                        disabled={false}
                        onChange={(key, val) => {
                          const newDevices = devices.map(d => {
                            if (d.id === selectedId) {
                              const updated = { ...d, props: { ...d.props, [key]: val } };
                              if (key === 'tankId' && (d.type === 'mixer' || (d.type === 'motor' && d.props['variant'] === 'mixer'))) {
                                const targetTank = devices.find(t => t.id === val);
                                if (targetTank) {
                                  updated.transform = {
                                    ...updated.transform,
                                    position: {
                                      x: (targetTank.transform?.position?.x ?? 0) + 40,
                                      y: (targetTank.transform?.position?.y ?? 0) - 48,
                                    },
                                  };
                                }
                              }
                              if (key === 'tankId' && d.type === 'level-sensor') {
                                const targetTank = devices.find(t => t.id === val);
                                if (targetTank) {
                                  updated.transform = {
                                    ...updated.transform,
                                    position: {
                                      x: (targetTank.transform?.position?.x ?? 0) + 72,
                                      y: (targetTank.transform?.position?.y ?? 0) - 18,
                                    },
                                  };
                                }
                              }
                              return updated;
                            }
                            return d;
                          });
                          useProjectStore.getState().setDevices(newDevices);
                        }}
                      />
                      {isAddressProp && (
                        <div
                          style={{
                            marginTop: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: 6,
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                          }}
                        >
                          <span style={{ fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🏷️</span> Adres Etiketini Göster
                          </span>
                          <input
                            type="checkbox"
                            checked={device?.props['showLabel'] !== false}
                            onChange={(e) => {
                              const newDevices = devices.map((d) => {
                                if (d.id === selectedId) {
                                  return { ...d, props: { ...d.props, showLabel: e.target.checked } };
                                }
                                return d;
                              });
                              useProjectStore.getState().setDevices(newDevices);
                            }}
                            style={{ cursor: 'pointer', accentColor: '#38bdf8', width: 16, height: 16 }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                <div style={{ marginTop: '20px' }}>
                  <Button
                    variant="primary"
                    onClick={() => {
                      const newDevices = devices.filter(d => d.id !== selectedId);
                      useProjectStore.getState().setDevices(newDevices);
                      useUiStore.getState().clearSelection();
                    }}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    Delete Component
                  </Button>
                </div>
                
                {device?.type === 'pid-controller' && (
                   <TrendGraph deviceId={selectedId as DeviceId} />
                )}
                {(device?.type === 'level-sensor' || device?.type === 'pump' || device?.type === 'valve') && (
                   <AnalogStatusMonitor deviceId={selectedId as DeviceId} />
                )}
              </div>
            ); })()
            ) : selectedType === 'pipe' ? (
              (() => {
                const pipe = pipes.find((p) => p.id === selectedId);
                if (!pipe) {
                  return (
                    <div style={{ marginTop: '20px' }}>
                      <Button
                        variant="primary"
                        onClick={() => {
                          removePipe(selectedId);
                          useUiStore.getState().clearSelection();
                        }}
                        style={{ width: '100%', justifyContent: 'center' }}
                      >
                        Boruyu Sil
                      </Button>
                    </div>
                  );
                }

                const fromDev = devices.find((d) => d.id === pipe.from.deviceId);
                const toDev = devices.find((d) => d.id === pipe.to.deviceId);
                const waypointCount = pipe.waypoints?.length ?? 0;
                const diameter = pipe.diameter ?? 8;
                const pipeColor = pipe.color ?? '#475569';

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
                    <div style={{ background: 'var(--color-bg-secondary, #1e293b)', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border, #334155)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #94a3b8)', marginBottom: '8px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Bağlantı Portları
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12.5px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--color-text-secondary, #94a3b8)' }}>Çıkış:</span>
                          <strong>{fromDev?.name ?? pipe.from.deviceId} ({pipe.from.portType})</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--color-text-secondary, #94a3b8)' }}>Giriş:</span>
                          <strong>{toDev?.name ?? pipe.to.deviceId} ({pipe.to.portType})</strong>
                        </div>
                      </div>
                    </div>

                    <div style={{ background: 'var(--color-bg-secondary, #1e293b)', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border, #334155)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #94a3b8)', marginBottom: '8px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Dirsekler (Köşe Dirsekleri)
                      </div>
                      <div style={{ fontSize: '12.5px', marginBottom: '10px' }}>
                        {waypointCount > 0 ? (
                          <span style={{ color: '#38bdf8', fontWeight: 500 }}>✓ {waypointCount} adet özel dirsek konumu aktif</span>
                        ) : (
                          <span style={{ color: 'var(--color-text-secondary, #94a3b8)' }}>Otomatik endüstriyel dik dirsekler</span>
                        )}
                      </div>
                      {waypointCount > 0 && (
                        <Button
                          variant="secondary"
                          onClick={() => {
                            updatePipe(pipe.id, { waypoints: [] });
                          }}
                          style={{ width: '100%', justifyContent: 'center', fontSize: '12px' }}
                        >
                          Dirsekleri Sıfırla (Otomatik Hat)
                        </Button>
                      )}
                    </div>

                    <div style={{ background: 'var(--color-bg-secondary, #1e293b)', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border, #334155)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #94a3b8)', marginBottom: '8px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Boru Çapı
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                        {[6, 8, 10, 12].map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => updatePipe(pipe.id, { diameter: d })}
                            style={{
                              padding: '6px 0',
                              fontSize: '12px',
                              borderRadius: '6px',
                              border: diameter === d ? '2px solid var(--color-primary, #3b82f6)' : '1px solid var(--color-border, #334155)',
                              background: diameter === d ? 'rgba(59, 130, 246, 0.2)' : 'var(--color-bg-elevated, #0f172a)',
                              color: diameter === d ? '#38bdf8' : 'var(--color-text-primary, #f8fafc)',
                              cursor: 'pointer',
                              fontWeight: diameter === d ? 'bold' : 'normal',
                            }}
                          >
                            {d}px
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ background: 'var(--color-bg-secondary, #1e293b)', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border, #334155)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #94a3b8)', marginBottom: '8px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Boru Malzemesi / Rengi
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                        {[
                          { label: 'Çelik Gri', color: '#475569' },
                          { label: 'Paslanmaz', color: '#64748b' },
                          { label: 'Koyu Döküm', color: '#334155' },
                          { label: 'Bakır', color: '#b45309' },
                        ].map((m) => (
                          <button
                            key={m.color}
                            type="button"
                            onClick={() => updatePipe(pipe.id, { color: m.color })}
                            style={{
                              padding: '6px 8px',
                              fontSize: '12px',
                              borderRadius: '6px',
                              border: pipeColor === m.color ? '2px solid var(--color-primary, #3b82f6)' : '1px solid var(--color-border, #334155)',
                              background: 'var(--color-bg-elevated, #0f172a)',
                              color: 'var(--color-text-primary, #f8fafc)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <span style={{ width: 12, height: 12, borderRadius: '50%', background: m.color, border: '1px solid rgba(255,255,255,0.3)' }} />
                            <span>{m.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginTop: '6px' }}>
                      <Button
                        variant="primary"
                        onClick={() => {
                          removePipe(selectedId);
                          useUiStore.getState().clearSelection();
                        }}
                        style={{ width: '100%', justifyContent: 'center' }}
                      >
                        Boruyu Sil
                      </Button>
                    </div>
                  </div>
                );
              })()
            ) : selectedType === 'ladderElement' ? (
              <div style={{ marginTop: '20px' }}>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!ladderElement || !ladderNetworkId) return;
                    const newProgram = {
                      ...program,
                      networks: program.networks.map(n => n.id === ladderNetworkId ? {
                        ...n,
                        elements: Object.fromEntries(Object.entries(n.elements).filter(([k]) => k !== ladderElement!.id))
                      } : n)
                    };
                    useLadderEditorStore.getState().executeCommand(newProgram);
                    useUiStore.getState().clearSelection();
                  }}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  Elemanı Sil
                </Button>
              </div>
            ) : (
              <p className={styles.note}>{t('properties.noSchema')}</p>
            )}
          </div>
          )
        ) : (
          <EmptyState
            icon="info"
            title={t('properties.empty.title')}
            description={t('properties.empty.description')}
          />
        )}
      </PanelBody>
    </Panel>
  );
}
